import { BASE_CAPACITY, formatWeight, getCapacityBonus, getWeight } from '../../../game/demo/inventory/capacity';
import { items, ItemId } from '../../../game/demo/inventory/items';
import eventBus from '../../utils/emitter-event-bus';
import { InventoryEventBus, InventoryReader } from '../inventory/inventory.model';

// Always-visible carry indicator, sibling to the coin HUD. Shows the current
// carry vessel as a symbol plus load / capacity. The vessel symbol is a
// placeholder for a future tier system (backpack, then donkey/horse/cart).
export class CarryHud {
  private readonly element = document.querySelector<HTMLElement>('.js-carry-hud');
  // Per-item snapshot of carried goods. We bump on ANY movement (a quantity
  // going up or down, an item appearing or vanishing), not just the total count:
  // fishing consumes a worm and grants a fish in one change, so the total can
  // stay flat while goods still moved. Coins / vessels are excluded (own HUDs).
  // Weight is irrelevant, so weightless movement (rod, charm) bumps too.
  private quantities: Partial<Record<ItemId, number>> = {};

  constructor(private readonly reader: InventoryReader) {
    this.quantities = this.snapshot();
    eventBus.on(InventoryEventBus.changed, () => this.onChange());
    this.render();
  }

  private snapshot(): Partial<Record<ItemId, number>> {
    const owned = this.reader.getState().items;
    const snap: Partial<Record<ItemId, number>> = {};
    for (const id of Object.keys(owned) as ItemId[]) {
      if (!items[id]?.hideInInventory) snap[id] = owned[id];
    }
    return snap;
  }

  private onChange(): void {
    const next = this.snapshot();
    const ids = new Set<ItemId>([...Object.keys(this.quantities), ...Object.keys(next)] as ItemId[]);
    const moved = [...ids].some((id) => (this.quantities[id] ?? 0) !== (next[id] ?? 0));
    if (moved) this.bump();
    this.quantities = next;
    this.render();
  }

  // Restart the pulse animation (remove + reflow + re-add) so rapid pickups
  // each get their own bump rather than being swallowed mid-animation.
  private bump(): void {
    if (!this.element) return;
    this.element.classList.remove('is-bump');
    void this.element.offsetWidth;
    this.element.classList.add('is-bump');
  }

  private vessel(): string {
    return this.reader.getState().items.backpack ? '🎒' : '👜';
  }

  private render(): void {
    if (!this.element) return;
    const state = this.reader.getState();
    const total = BASE_CAPACITY + getCapacityBonus(state);
    this.element.textContent = `${this.vessel()} ${formatWeight(getWeight(state))}/${total}`;
  }
}
